/**
 * AuthorisationUtils - Site-level authorization utilities
 *
 * MSAL helpers (`isSiteAdmin`, `isSiteAdminOrSupport`) check B2C custom claims
 * (idTokenClaims.extension_UserRole). They are used by ProtectedRoute for
 * route-level authorization.
 *
 * **For React components:** Prefer using the `useSitePermissions()` hook instead,
 * which handles dual-provider (MSAL/Kinde) support automatically.
 *
 * `isInternalIntercomUser` is provider-agnostic and accepts pre-computed inputs
 * (user object + role flag), so it can be called from any context.
 *
 * @module AuthorisationUtils
 * @see {@link useSitePermissions} for component-level permission checking
 */

const INTERCOM_INTERNAL_EMAIL_DOMAINS = ['@tempustools.com', '@ipcompute.com']

/**
 * Site-level user roles from Azure AD B2C custom claims
 */
export const UserRole = {
    SiteAdmin: 'SiteAdmin',
    SiteSupport: 'SiteSupport',
}

/**
 * Returns true if the user has SiteAdmin role (MSAL/B2C only)
 *
 * Checks the `extension_UserRole` custom claim in MSAL account's idTokenClaims.
 * For React components, prefer using `useSitePermissions().isSiteAdmin` instead.
 *
 * @param {Object} accountInfo - MSAL account info with idTokenClaims
 * @param {Object} [accountInfo.idTokenClaims] - Token claims from B2C
 * @param {string} [accountInfo.idTokenClaims.extension_UserRole] - User role claim
 * @returns {boolean} True if account has UserRole === 'SiteAdmin'
 *
 * @example
 * // In ProtectedRoute (non-hook context)
 * const hasAccess = isSiteAdmin(user)
 */
export function isSiteAdmin(accountInfo) {
    return accountInfo?.idTokenClaims?.extension_UserRole === UserRole.SiteAdmin
}

/**
 * Returns true if the user has SiteAdmin OR SiteSupport role (MSAL/B2C only)
 *
 * Checks the `extension_UserRole` custom claim in MSAL account's idTokenClaims.
 * For React components, prefer using `useSitePermissions().isSiteAdminOrSupport` instead.
 *
 * @param {Object} accountInfo - MSAL account info with idTokenClaims
 * @param {Object} [accountInfo.idTokenClaims] - Token claims from B2C
 * @param {string} [accountInfo.idTokenClaims.extension_UserRole] - User role claim
 * @returns {boolean} True if account has UserRole === 'SiteAdmin' OR 'SiteSupport'
 *
 * @example
 * // In ProtectedRoute (non-hook context)
 * const hasAccess = isSiteAdminOrSupport(user)
 */
export function isSiteAdminOrSupport(accountInfo) {
    return (
        accountInfo?.idTokenClaims?.extension_UserRole === UserRole.SiteAdmin ||
        accountInfo?.idTokenClaims?.extension_UserRole === UserRole.SiteSupport
    )
}

/**
 * Returns true if the user should be treated as internal Tempus Tools staff
 * for the purpose of the Intercom integration.
 *
 * Used to prevent SiteAdmin/SiteSupport users (and anyone with a Tempus Tools
 * email domain) from being attached as contacts of customer organisations in
 * Intercom — which otherwise pollutes customer "last seen" metrics and inflates
 * contact counts.
 *
 * Detection uses two signals belt-and-suspenders:
 *   1. Site-level role flag (typically from `useSitePermissions().isSiteAdminOrSupport`),
 *      since the authoritative role source varies by auth provider (MSAL claim vs
 *      Kinde async permission).
 *   2. Email-domain whitelist, as a synchronous backstop in case the role flag
 *      hasn't resolved yet on first boot (Kinde permission fetch is async).
 *
 * @param {Object} [user] - User object
 * @param {string} [user.emailAddress] - User's email address
 * @param {boolean} [isSiteAdminOrSupport=false] - Pre-computed site role flag
 * @returns {boolean} True if the user is internal staff
 */
export function isInternalIntercomUser(user, isSiteAdminOrSupport = false) {
    if (isSiteAdminOrSupport) return true
    const email = user?.emailAddress?.toLowerCase()
    if (!email) return false
    return INTERCOM_INTERNAL_EMAIL_DOMAINS.some((domain) => email.endsWith(domain))
}
